import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { type ProjectTimelineNoteSchema, projectTimelineNoteSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ProjectTimelineNoteForm } from "@/forms/project-timeline-note-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditProjectTimelineNoteModalProps {
	isOpen: boolean;
	onClose: () => void;
	projectId: string;
	noteId: string;
	onComplete: () => Promise<void>;
}

export function EditProjectTimelineNoteModal({
	isOpen,
	onClose,
	projectId,
	noteId,
	onComplete,
}: EditProjectTimelineNoteModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.projects[":projectId"].timeline.notes[":noteId"].$get({
			param: {
				projectId,
				noteId,
			},
		});
		const data = await responseHandler(response);
		if (!data.note) return {} as ProjectTimelineNoteSchema;
		return {
			isImportant: data.note?.isImportant || false,
			date: moment(data.timelineNote?.date).db(),
			note: data.note.note,
		};
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(projectTimelineNoteSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: ProjectTimelineNoteSchema) {
		try {
			await client.projects[":projectId"].timeline.notes[":noteId"].$patch({
				param: { projectId, noteId },
				json,
			});
			onClose();
			await onComplete();
		} catch (error) {
			console.error("Error editing note:", error);
			toast.error(t("general.something_went_wrong"));
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				onClose={onClose}
				isOpen={isOpen}
				title={t("notes.edit_note")}
				width="700px"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
				data-cy="project-timeline-note-form"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ProjectTimelineNoteForm controller={null} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
